import { NgModule } from '@angular/core';
import { RouteReuseStrategy, RouterModule, Routes } from '@angular/router';

// Components
import { AdminLevel } from '@blaze-shared/api-contracts-domain';
import { AdminLevelGuard, AuthTokenGuard, ExternalUserGuard } from '@blaze-shared/auth-data-access';
import { PageNotFoundComponent } from '@blaze-shared/shared-ui';

// Services
import { redirectOnCustomCname } from './app-redirects';
import { oAuthRouteMatcher } from './app-routing.matchers';
import { CustomRouteReuseStrategy } from './app-routing.strategy';
import { IsAccountInitializedGuard } from './routing/is-account-initialized-guard.service';
import { RuntimeModeResolverService } from './runtime-mode-resolver';
import { StaticRedirectGuardService } from './static-redirect-guard.service';

const routes: Routes = [
  {
    path: 'acceptinvite',
    loadChildren: () => import('@blaze-shared/invite-feature').then((m) => m.InviteModule),
  },
  {
    path: 'verifyemail',
    loadChildren: () =>
      import('@blaze-shared/verify-email-feature').then((m) => m.VerifyEmailModule),
  },
  {
    path: 'autologin',
    loadComponent: () => import('@blaze-shared/auth-feature').then((m) => m.AutologinComponent),
    data: {
      // We set actionBar explicitly to null in order to hide Blaze's default action bar.
      actionBar: null,
    },
  },
  {
    path: 'login',
    loadChildren: () => import('@blaze-shared/auth-feature').then((m) => m.AuthModule),
  },
  {
    path: 'certlogin',
    loadChildren: () => import('@blaze-shared/auth-feature').then((m) => m.AuthModule),
  },
  {
    path: 'signup',
    loadChildren: () =>
      import('@blaze-runtime/external-signup-feature').then((m) => m.ExternalSignupModule),
  },
  {
    matcher: oAuthRouteMatcher,
    loadChildren: () => import('@blaze-shared/oauth-feature').then((m) => m.OAuthModule),
    canLoad: [AuthTokenGuard],
    data: {
      // We set actionBar explicitly to null in order to hide Blaze's default action bar.
      actionBar: null,
    },
  },
  {
    path: 'updatedevaccess',
    loadChildren: () =>
      import('@blaze-dashboard/dev-access-feature').then((m) => m.DevAccessModule),
  },
  // Edit project
  {
    path: 'projects',
    loadChildren: () => import('@blaze-dashboard/projects-feature').then((m) => m.ProjectsModule),
    canLoad: [
      // We add redirect logic here because any root URL will redirect to /projects.
      // TODO(michal): once we upgrade to Angular 18, we can implement this logic in the redirectTo rounte.
      StaticRedirectGuardService,
      AuthTokenGuard,
    ],
    canActivate: [AdminLevelGuard, ExternalUserGuard],
    resolve: {
      runtimeMode: RuntimeModeResolverService,
    },
  },
  {
    path: 'preview', // Preview Project
    loadChildren: () => import('@blaze-runtime/view-feature').then((m) => m.ProjectViewModule),
    canLoad: [AuthTokenGuard],
    resolve: {
      runtimeMode: RuntimeModeResolverService,
    },
  },
  // Published project
  {
    path: 'app', // View Project
    loadChildren: () => import('@blaze-runtime/view-feature').then((m) => m.ProjectViewModule),
    canLoad: [IsAccountInitializedGuard],
    resolve: {
      runtimeMode: RuntimeModeResolverService,
    },
  },
  // Public and Public Secure projects
  {
    path: 'p', // View Project
    loadChildren: () => import('@blaze-runtime/view-feature').then((m) => m.ProjectViewModule),
    canLoad: [IsAccountInitializedGuard],
    resolve: {
      runtimeMode: RuntimeModeResolverService,
    },
  },
  {
    path: 'public', // View Project @deprecated
    loadChildren: () => import('@blaze-runtime/view-feature').then((m) => m.ProjectViewModule),
    canLoad: [IsAccountInitializedGuard],
    resolve: {
      runtimeMode: RuntimeModeResolverService,
    },
  },
  {
    path: 'resetpassword',
    loadComponent: () =>
      import('@blaze-shared/reset-password-feature').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'users',
    loadChildren: () => import('@blaze-dashboard/users-feature').then((m) => m.UsersModule),
    canLoad: [AuthTokenGuard],
    canActivate: [AdminLevelGuard, ExternalUserGuard],
    data: {
      adminLevel: AdminLevel.Admin as AdminLevel,
    },
  },
  {
    path: 'account',
    loadChildren: () => import('@blaze-dashboard/account-feature').then((m) => m.AccountModule),
    canLoad: [AuthTokenGuard],
    canActivate: [ExternalUserGuard],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: (routeSnapshot) => redirectOnCustomCname(routeSnapshot),
    canLoad: [AuthTokenGuard],
  },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false, // set to true for debugging purposes
      paramsInheritanceStrategy: 'always', // url params are flattened
    }),
  ],
  exports: [RouterModule],
  providers: [
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    IsAccountInitializedGuard,
  ],
})
export class AppRoutingModule {}
